<template>
  <div :class="$style.container">
    <div :class="$style.titleSection">
      <div :class="$style.title">New Responses</div>
    </div>

    <div :class="$style.contentSection">
      <div :class="$style.action">
        <SiteFilter
          :class="$style.site"
          v-if="siteList.length > 0"
          :auditSites="siteList"
          @applySiteFilter="applySiteFilter"
        ></SiteFilter>

        <CriteriaFilter
          :class="$style.criteria"
          v-if="criteriaList && criteriaList.length > 0"
          :auditCriteria="criteriaList"
          @applyCriteriaFilter="applyCriteriaFilter"
        />

        <div class="field" :class="$style.period">
          <label class="label">Period</label>
          <div class="control">
            <b-datepicker
              v-model="selectedPeriod"
              :max-date="maxDate"
              :first-day-of-week="1"
              placeholder="Click to select..."
              range
              :date-formatter="formatDateRange"
              @input="filterPeriod"
              icon="calendar-today"
              icon-pack="mdi"
              :class="[$style.customCalendarIcon, 'calender-icon']"
            >
            </b-datepicker>
          </div>
        </div>
      </div>

      <div :class="$style.chartContainer">
        <VerticalBarChart
          :key="barChartKey"
          :class="$style.chart"
          :barChartData="chartData"
          :barChartOptions="options"
        ></VerticalBarChart>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { State, Action } from 'vuex-class';
import { RootState } from '@/store/store';
import {
  AuditCriteria,
  AuditDetail,
  AuditSite,
  DashboardChartData,
  DashboardChartDataRequestPayload,
} from '@/store/modules/audits/types/audits.types';
import SiteFilter from '@/views/Audit/components/SiteFilter.vue';
import CriteriaFilter from '@/views/Audit/components/CriteriaFilter.vue';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import VerticalBarChart from '@/components/reports/VerticalBarChart';
import { useUserEmail } from '@/utils/user.util';
const dirtyMonths = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

@Component({
  components: {
    VerticalBarChart,
    CriteriaFilter,
    SiteFilter,
  },
})
export default class ChartView extends Vue {
  @Action('audits/getAuditCriteria')
  public getAuditCriteria!: (auditId: number) => void;

  @Action('audits/getDashboardChartData')
  public getDashboardChartData!: (
    payload: DashboardChartDataRequestPayload,
  ) => void;

  @Prop() public auditSites!: AuditSite[];

  @State((state: RootState) => state.audits.auditDetail)
  public auditDetail!: AuditDetail;

  @State((state: RootState) => state.audits.auditCriteria)
  public auditCriteria!: AuditCriteria[];

  @State((state: RootState) => state.audits.dashboardChartData)
  public dashboardChartData!: DashboardChartData[];

  public today = new Date();
  public maxDate = new Date();
  public selectedCriteriaIds: number[] = [];
  public selectedAuditSiteIds: number[] = [];
  public todayDate: Date = new Date();
  public selectedPeriod: Date[] = [
    new Date(
      this.todayDate.getFullYear(),
      this.todayDate.getMonth(),
      this.todayDate.getDate() - 7,
    ),
    this.todayDate,
  ];
  public barChartKey: number = Math.floor(Math.random() * 999);

  public chartData = {
    labels: [],
    datasets: [
      {
        label: 'Total Data Collected',
        data: [],
        backgroundColor: Array(0).fill('#1D445E'),
        barThickness: 60,
      },
    ],
  };

  get auditId(): number {
    return +this.$route.params.auditId;
  }

  public options = {
    scales: {
      yAxes: [
        {
          ticks: {
            beginAtZero: true,
          },
          gridLines: {
            display: true,
          },
          scaleLabel: {
            labelString: 'New Responses',
            display: true,
          },
        },
      ],
      xAxes: [
        {
          gridLines: {
            display: false,
          },
          scaleLabel: {
            labelString: 'Date',
            display: true,
          },
        },
      ],
    },
    legend: {
      display: false,
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  get userEmail(): string | undefined {
    return useUserEmail.call(this);
  }

  public mounted() {
    this.selectedAuditSiteIds = this.auditSites.map(
      (auditSite) => auditSite.id,
    );
    this.getAuditCriteria(this.auditId);
  }

  @Watch('auditCriteria')
  @isTruthy
  public watchAuditCriteria() {
    this.selectedCriteriaIds = this.auditCriteria.map(
      (criteria) => criteria.id,
    );
    this.filterChartData();
  }

  get siteList(): any[] {
    if (!this.auditSites) {
      return [];
    }
    return this.auditSites.map((auditSite) => ({
      siteId: auditSite.site.id,
      name: auditSite.site.name,
      auditSiteId: auditSite.id,
    }));
  }

  public applySiteFilter(newValue: number[]) {
    this.selectedAuditSiteIds = newValue;
    this.filterChartData();
  }

  get criteriaList() {
    return this.auditCriteria;
  }

  public applyCriteriaFilter(newValue: number[]) {
    this.selectedCriteriaIds = newValue;
    this.filterChartData();
  }

  public filterPeriod(newValue: Date[]) {
    this.selectedPeriod = newValue;
    this.filterChartData();
  }

  public filterChartData() {
    const payload: DashboardChartDataRequestPayload = {
      auditId: this.auditId,
      fromDate: this.setTimeStart(this.selectedPeriod[0]),
      toDate: this.setTimeEnd(this.selectedPeriod[1]),
      siteIds: this.selectedAuditSiteIds,
      criteriaIds: this.selectedCriteriaIds,
    };

    this.getDashboardChartData(payload);
  }

  public setTimeEnd(date: Date) {
    date.setHours(23);
    date.setMinutes(59);
    date.setSeconds(59);
    return date;
  }

  public setTimeStart(date: Date) {
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    return date;
  }

  public formatDateRange(date: Date[]) {
    return this.getDate(date[0]) + ' - ' + this.getDate(date[1]);
  }

  public getDate(newValue: Date) {
    return (
      newValue.getDate() +
      ' ' +
      dirtyMonths[newValue.getMonth()] +
      ' ' +
      newValue.getFullYear()
    );
  }

  public accumulateNewResponseByDate(
    data: DashboardChartData[],
  ): DashboardChartData[] {
    const accumulatedData: DashboardChartData[] = [];
    data.reduce((prev, curr) => {
      const pureDate: string = new Date(curr.createdDate).toLocaleDateString();
      if (!prev[pureDate]) {
        prev[pureDate] = { pureDate, sum: 0, createdDate: curr.createdDate };
        accumulatedData.push(prev[pureDate]);
      }
      prev[pureDate].sum += +curr.sum;
      return prev;
    }, {} as any);
    return accumulatedData;
  }

  @Watch('dashboardChartData')
  @isTruthy
  public watchDashboardChartData(newValue: DashboardChartData[]) {
    const lables: any = [];
    const data: any = [];

    this.accumulateNewResponseByDate(newValue).forEach(
      (chartData: DashboardChartData) => {
        lables.push(this.getDate(new Date(chartData.createdDate)));
        data.push(chartData.sum);
      },
    );
    this.chartData = {
      labels: lables,
      datasets: [
        {
          label: 'Total Data Collected',
          data,
          backgroundColor: Array(data.length).fill('#1D445E'),
          barThickness: 60,
        },
      ],
    };
    this.barChartKey += 1;
  }
}
</script>

<style lang="scss" module>
.container {
  margin-bottom: 5rem;

  .titleSection {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .title {
      font-weight: bold;
      text-transform: uppercase;
      padding: 8px 0;
    }
  }

  .contentSection {
    box-shadow: 0 2px 2px $grey-border;
    border: 0.5px solid $grey-border;
    border-radius: 6px;
    background: white;
    // overflow-x: auto;
    padding: 1.5rem;

    .action {
      display: flex;
      .site {
        margin-right: 0 0.5rem;
      }

      .criteria {
        margin: 0 0.5rem;
      }

      .period {
        flex-grow: 4;
      }
    }

    .chartContainer {
      display: flex;
      padding-top: 20px;
      min-height: 300px;

      .chart {
        color: #444f51;
        font-size: 16px;
        height: 300px;
        width: 100%;
      }
    }
  }
}

.customCalendarIcon {
  i::before {
    padding-top: 13px;
    padding-bottom: 12px;
    width: 15px;
    margin-right: 10px;
  }
}

.dropdown {
  :global(.dropdown-menu) {
    padding: 0;

    :global(.dropdown-content) {
      padding: 0;

      :global(.dropdown-item) {
        padding: 10px;
      }
    }
  }

  .dropdownButton {
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;

    span {
      font-weight: normal;
    }
  }
}
</style>
